// @ts-nocheck
import { CELL, CHUNK, HOLE_W, HOLE_JITTER, MIN_HOLE_SEPARATION, BUCKET_SZ, MIN_VISIBLE_WORLD_Y } from "./constants";
import { mapState, mapRefs } from "./state";
import {
  hsh,
  srand,
  inTemple,
  registerHole,
  tooCloseToAnyHole,
  registerDecor,
  canPlaceDecor,
  tooCloseToAnyDecor,
} from "./spatial";
import { createPixelFlower } from "./flowers";
import { restoreGrave } from "./grave-render";
import { TEMPLE_R } from "./constants";

export function getHolePos(gx, gy) {
  const prob = srand(gx, gy, 42);
  if (prob > 0.55) return null;
  const wx = gx * CELL,
    wy = gy * CELL;
  const ox = (srand(gx, gy, 111) - 0.5) * HOLE_JITTER * 2;
  const oy = (srand(gx, gy, 222) - 0.5) * HOLE_JITTER * 2;
  const wave1 = Math.sin(gx * 0.7 + gy * 0.3) * 20;
  const wave2 = Math.cos(gx * 0.4 - gy * 0.6) * 15;
  const fx = wx + ox + wave1,
    fy = wy + oy + wave2;
  if (inTemple(fx, fy)) return null;
  if (fy < MIN_VISIBLE_WORLD_Y) return null;
  return { x: fx, y: fy };
}

export function ensureHolesRegistered(cx, cy) {
  const rk = "r" + cx + "," + cy;
  if (mapState.genChunks[rk]) return;
  mapState.genChunks[rk] = true;
  const sx = cx * CHUNK,
    sy = cy * CHUNK;
  for (let dy = 0; dy < CHUNK; dy++) {
    for (let dx = 0; dx < CHUNK; dx++) {
      const gx = sx + dx,
        gy = sy + dy;
      const p = getHolePos(gx, gy);
      if (!p) continue;
      if (tooCloseToAnyHole(p.x, p.y, MIN_HOLE_SEPARATION)) continue;
      registerHole(p.x, p.y);
    }
  }
}

/**
 * Calcola (una sola volta per chunk) le posizioni di buche, decorazioni e
 * fiori. I check spaziali e registerDecor NON sono idempotenti: rieseguirli
 * alla rigenerazione di un chunk scaricato farebbe collidere ogni elemento
 * con la propria registrazione precedente. Il risultato è cache-ato per
 * sempre in mapState.chunkPlacements.
 */
export function computeChunkPlacements(cx, cy) {
  const ck = cx + "," + cy;
  const cached = mapState.chunkPlacements[ck];
  if (cached) return cached;

  for (let ay = cy - 1; ay <= cy + 1; ay++)
    for (let ax = cx - 1; ax <= cx + 1; ax++) ensureHolesRegistered(ax, ay);

  const sx = cx * CHUNK,
    sy = cy * CHUNK;
  const chunkPx = CHUNK * CELL;
  const placements = { holes: [], decors: [], flowers: [] };

  // Buchi
  for (let dy = 0; dy < CHUNK; dy++) {
    for (let dx = 0; dx < CHUNK; dx++) {
      const gx = sx + dx,
        gy = sy + dy;
      const p = getHolePos(gx, gy);
      if (!p) continue;

      if (tooCloseToAnyHole(p.x, p.y, MIN_HOLE_SEPARATION)) {
        let isOwn = false;
        const bx = Math.floor(p.x / BUCKET_SZ),
          by = Math.floor(p.y / BUCKET_SZ);
        const arr = mapState.holeBuckets[bx + "," + by];
        if (arr) {
          for (let k = 0; k < arr.length; k++) {
            if (
              Math.abs(arr[k].x - p.x) < 0.1 &&
              Math.abs(arr[k].y - p.y) < 0.1
            ) {
              isOwn = true;
              break;
            }
          }
        }
        if (!isOwn) continue;
      }

      placements.holes.push({ hid: gx + "," + gy, x: p.x, y: p.y });
    }
  }

  // Decorazioni
  const nd = 5 + (hsh(cx, cy, 9876) % 5);
  for (let i = 0; i < nd; i++) {
    const rx = srand(cx * 100 + i, cy * 200 + i, 3333);
    const ry = srand(cx * 300 + i, cy * 400 + i, 4444);
    const rt = srand(cx * 500 + i, cy * 600 + i, 5555);
    const rs = srand(cx * 900 + i, cy * 1100 + i, 7777);

    const cwx = cx * chunkPx + rx * chunkPx;
    const cwy = cy * chunkPx + ry * chunkPx;

    const isTree = rt > 0.35;
    const sz = isTree ? 55 + Math.floor(rs * 45) : 25 + Math.floor(rs * 30);
    const halfSz = sz / 2;

    if (Math.abs(cwx) < TEMPLE_R - 30 && Math.abs(cwy) < TEMPLE_R - 30)
      continue;
    if (!canPlaceDecor(cwx, cwy, halfSz)) continue;

    registerDecor(cwx, cwy, halfSz);
    placements.decors.push({ x: cwx, y: cwy, sz, isTree });
  }

  // Fiori - MOLTI PIÙ FIORI E PIÙ BELLI
  const nf = 10 + (hsh(cx, cy, 5432) % 9); // Da 10 a 18 fiori per chunk
  for (let i = 0; i < nf; i++) {
    const fx = srand(cx * 150 + i, cy * 250 + i, 8888);
    const fy = srand(cx * 350 + i, cy * 450 + i, 9999);
    const fsz = 10 + Math.floor(srand(cx * 550 + i, cy * 650 + i, 1234) * 14); // Da 10 a 24px
    const fci = hsh(cx + i, cy + i, 4321) % 12; // 12 varietà di colori

    const fwx = cx * chunkPx + fx * chunkPx;
    const fwy = cy * chunkPx + fy * chunkPx;

    // NON sul tempio (area più ampia)
    if (Math.abs(fwx) < TEMPLE_R - 20 && Math.abs(fwy) < TEMPLE_R - 20)
      continue;

    // NON vicino a tombe/buche
    const flowerHoleDist = Math.round(MIN_HOLE_SEPARATION * 0.65);
    if (tooCloseToAnyHole(fwx, fwy, flowerHoleDist)) continue;

    // NON vicino ad alberi/sassi (distanza aumentata)
    if (tooCloseToAnyDecor(fwx, fwy, 30)) continue; // Aumentato da 20 a 30

    registerDecor(fwx, fwy, fsz / 2);
    placements.flowers.push({ x: fwx, y: fwy, sz: fsz, ci: fci });
  }

  mapState.chunkPlacements[ck] = placements;
  return placements;
}

export function genChunk(cx, cy) {
  const ck = cx + "," + cy;
  if (mapState.genChunks[ck]) return;
  const world = mapRefs.world;
  if (!world) return;
  mapState.genChunks[ck] = true;

  const placements = computeChunkPlacements(cx, cy);
  const els = (mapState.chunkEls[ck] ||= []);

  for (const ph of placements.holes) {
    if (mapState.holeEls[ph.hid]) continue;
    const h = document.createElement("div");
    h.className = "hole-spot";
    h.dataset.id = ph.hid;
    h.style.left = ph.x - HOLE_W / 2 + "px";
    h.style.top = ph.y - HOLE_W / 2 + "px";
    world.appendChild(h);
    mapState.holeEls[ph.hid] = h;
    els.push(h);
    if (mapState.gravesData[ph.hid]) restoreGrave(h, mapState.gravesData[ph.hid]);
  }

  for (const d of placements.decors) {
    const halfSz = d.sz / 2;
    const el = document.createElement("div");
    el.className = "decor " + (d.isTree ? "tree" : "stone");
    el.style.left = d.x - halfSz + "px";
    el.style.top = d.y - halfSz + "px";
    el.style.width = d.sz + "px";
    world.appendChild(el);
    els.push(el);
  }

  for (const f of placements.flowers) {
    const flower = createPixelFlower(f.sz, f.ci);
    flower.style.left = f.x - f.sz / 2 + "px";
    flower.style.top = f.y - f.sz + "px";
    world.appendChild(flower);
    els.push(flower);
  }

  // Buche manuali (fallback per lapidi backend, vedi holes.ts): il loro
  // placement è già stato validato alla prima creazione.
  const manual = mapState.manualHoles[ck];
  if (manual) {
    for (const m of manual) {
      if (mapState.holeEls[m.hid]) continue;
      const h = document.createElement("div");
      h.className = "hole-spot";
      h.dataset.id = m.hid;
      h.style.left = m.x - HOLE_W / 2 + "px";
      h.style.top = m.y - HOLE_W / 2 + "px";
      world.appendChild(h);
      mapState.holeEls[m.hid] = h;
      els.push(h);
      if (mapState.gravesData[m.hid]) restoreGrave(h, mapState.gravesData[m.hid]);
    }
  }
}

/**
 * Smonta dal DOM un chunk fuori viewport. I dati spaziali (holeBuckets,
 * decorBuckets, chunkPlacements, manualHoles) e gravesData restano intatti:
 * muore solo il DOM, la rigenerazione via genChunk è idempotente.
 * Ritorna false (nessun unload) se il chunk contiene elementi che il resto
 * dell'app sta referenziando (flusso acquisto, view modal).
 */
export function unloadChunk(ck) {
  const els = mapState.chunkEls[ck];
  if (!els || els.length === 0) {
    delete mapState.chunkEls[ck];
    delete mapState.genChunks[ck];
    return true;
  }

  const protectedEls = new Set();
  if (mapState.selectedHole) protectedEls.add(mapState.selectedHole);
  if (mapState.pendingPurchaseHole) protectedEls.add(mapState.pendingPurchaseHole);
  const viewing = window.__viewingHeadstone;
  if (viewing?.holeId && mapState.holeEls[viewing.holeId]) {
    protectedEls.add(mapState.holeEls[viewing.holeId]);
  }
  const editing = window.__editingHeadstone;
  if (editing?.holeId && mapState.holeEls[editing.holeId]) {
    protectedEls.add(mapState.holeEls[editing.holeId]);
  }

  if (protectedEls.size > 0) {
    for (const el of els) {
      if (protectedEls.has(el)) return false;
    }
  }

  for (const el of els) {
    const hid = el.dataset?.id;
    if (hid) delete mapState.holeEls[hid];
    el.remove();
  }
  delete mapState.chunkEls[ck];
  delete mapState.genChunks[ck];
  return true;
}
